import React from 'react';
import { Link } from 'react-router-dom';

/**
 * WORK GRID.
 *
 * Every card is the same shape. Homepage screenshots run from about 1.5 to
 * 1.78, so showing them at natural height produced a ragged grid with cards
 * ending at different points. The frame is 16/9 on purpose: wider than the
 * widest screenshot we hold, so `object-cover` always crops vertically rather
 * than horizontally. Every card shows the full width of the design and, with
 * `object-top`, the top of the page.
 *
 * Every card opens the running site itself, in a new tab. All seven examples
 * now have one, so there is no reason to route a reader through a write-up
 * first: the site is the example. The `/work/<slug>` page still exists for
 * search and for anyone who wants the screenshot, reachable from the title.
 */

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

export function WorkCard({ study, eager = false }) {
  const host = study.liveUrl ? hostOf(study.liveUrl) : null;

  return (
    <article className="group">
      <a
        href={study.liveUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={`${study.title} — open the site`}
        className="block overflow-hidden rounded-[14px] border border-[#0A1A2F]/[0.12] bg-white transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-1"
        style={{ boxShadow: '0 24px 60px -46px rgba(10,26,47,0.6)' }}
      >
        <span className="flex items-center gap-2 border-b border-[#0A1A2F]/[0.1] bg-[#EFEBE3] px-4 py-2.5">
          <i aria-hidden className="h-[7px] w-[7px] rounded-full bg-[#0A1A2F]/[0.16]" />
          <i aria-hidden className="h-[7px] w-[7px] rounded-full bg-[#0A1A2F]/[0.12]" />
          <i aria-hidden className="h-[7px] w-[7px] rounded-full bg-[#0A1A2F]/[0.09]" />
          <small className="ml-2 truncate font-mono-label text-[10px] tracking-[0.06em] text-[#3F3A30]/50">
            {host || study.sector}
          </small>
        </span>

        <div className="relative aspect-[16/9] w-full overflow-hidden bg-[#EFEBE3]">
          <img
            src={study.image}
            alt={study.alt}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03]"
          />
          {/* the invitation, only on hover, so the card stays a picture until you want more */}
          <span className="pointer-events-none absolute inset-0 flex items-end justify-start bg-gradient-to-t from-[#0A1A2F]/70 via-transparent to-transparent p-5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:p-6">
            <span className="inline-flex items-center gap-2.5 rounded-full bg-[#F5F3EF] px-4 py-2 text-[12px] font-semibold tracking-[0.06em] text-[#0A1A2F]">
              See it live
              <span aria-hidden>↗</span>
            </span>
          </span>
        </div>
      </a>

      <div className="mt-5">
        <h3 className="font-serif text-[clamp(1.2rem,2vw,1.45rem)] leading-tight text-[#0A1A2F]">
          <Link to={`/work/${study.slug}`} className="transition-colors hover:text-[#8C6C2D]">
            {study.title}
          </Link>
        </h3>
        <p className="mt-2 max-w-[42ch] text-[15px] leading-[1.5] text-[#3F3A30]/80">{study.summary}</p>

        <a
          href={study.liveUrl}
          target="_blank"
          rel="noreferrer"
          className="group/link mt-4 inline-flex items-center gap-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#8C6C2D] transition-colors hover:text-[#0A1A2F]"
        >
          See it live
          <span aria-hidden className="transition-transform duration-300 group-hover/link:translate-x-1">↗</span>
        </a>
      </div>
    </article>
  );
}

export default function WorkGrid({ studies }) {
  return (
    <div className="grid grid-cols-1 items-start gap-x-8 gap-y-14 sm:grid-cols-2">
      {studies.map((study, index) => (
        <WorkCard key={study.slug} study={study} eager={index === 0} />
      ))}
    </div>
  );
}
